/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  createComment,
  deleteComment,
  filterComments,
  getCommentById,
  getCommentReplies,
  getCommentsByCommentableId,
  getCommentsWithReplies,
  updateComment,
} from './comment.service';
import { ICreateComment, IUpdateComment } from './comment.d';
import { auditKeys } from '../audit/audit.hooks';

// Query key constants
const QUERY_KEYS = {
  COMMENT: 'comment',
  COMMENTS: 'comments',
  BY_ID: 'by-id',
  BY_COMMENTABLE: 'by-commentable',
  WITH_REPLIES: 'with-replies',
  REPLIES: 'replies',
  BY_PARENT: 'by-parent',
} as const;

// Query key factories following best practices
const commentKeys = {
  all: () => [QUERY_KEYS.COMMENT] as const,
  comments: () => [...commentKeys.all(), QUERY_KEYS.COMMENTS] as const,
  comment: () => [...commentKeys.all(), QUERY_KEYS.BY_ID] as const,
  commentById: (id: string) => [...commentKeys.comment(), id] as const,
  commentsByCommentable: (commentableId: string, commentableType?: string) =>
    [
      ...commentKeys.comments(),
      QUERY_KEYS.BY_COMMENTABLE,
      commentableId,
      commentableType || 'all',
    ] as const,
  commentsWithReplies: (commentableId: string, commentableType?: string) =>
    [
      ...commentKeys.comments(),
      QUERY_KEYS.WITH_REPLIES,
      commentableId,
      commentableType || 'all',
    ] as const,
  replies: () => [...commentKeys.all(), QUERY_KEYS.REPLIES] as const,
  repliesByParent: (parentId: string) =>
    [...commentKeys.replies(), QUERY_KEYS.BY_PARENT, parentId] as const,
};

// Hook to get comment by ID
export const useGetCommentById = (id?: string) => {
  return useQuery({
    queryKey: commentKeys.commentById(id || ''),
    queryFn: () => getCommentById(id),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: Boolean(id),
  });
};

// Hook to get comments by commentable ID with optional search filtering
export const useGetCommentsByCommentableId = (
  commentableId?: string,
  commentableType?: 'DatabaseEntity' | 'DatabaseProduct',
  searchTerm?: string,
) => {
  const {
    data: allComments,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: commentKeys.commentsByCommentable(commentableId || '', commentableType),
    queryFn: () => getCommentsByCommentableId(commentableId, commentableType),
    staleTime: 2 * 60 * 1000,
    enabled: Boolean(commentableId),
  });

  const filteredComments = useMemo(() => {
    if (!allComments) {
      return [];
    }
    return filterComments(allComments, searchTerm || '');
  }, [allComments, searchTerm]);

  return {
    data: filteredComments,
    isLoading: isLoading,
    error: error,
    refetch: refetch,
  };
};

// Hook to get comments with nested replies
export const useGetCommentsWithReplies = (
  commentableId?: string,
  commentableType?: 'DatabaseEntity' | 'DatabaseProduct',
  searchTerm?: string,
) => {
  const {
    data: allComments,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: commentKeys.commentsWithReplies(commentableId || '', commentableType),
    queryFn: () => getCommentsWithReplies(commentableId, commentableType),
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: Boolean(commentableId),
  });

  const filteredComments = useMemo(() => {
    if (!allComments) {
      return [];
    }
    return filterComments(allComments, searchTerm || '');
  }, [allComments, searchTerm]);

  return {
    data: filteredComments,
    isLoading: isLoading,
    error: error,
    refetch: refetch,
  };
};

// Hook to get replies to a specific comment
export const useGetCommentReplies = (parentId?: string) => {
  return useQuery({
    queryKey: commentKeys.repliesByParent(parentId || ''),
    queryFn: () => getCommentReplies(parentId),
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: Boolean(parentId),
  });
};

// Mutation hook to create comment
export const useCreateComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (commentData: ICreateComment) => createComment(commentData),
    onSuccess: (data, variables) => {
      // Invalidate and refetch comment-related queries
      queryClient.invalidateQueries({ queryKey: commentKeys.all() });

      // If we have the commentable info, invalidate specific queries
      if (variables.commentable_id) {
        queryClient.invalidateQueries({
          queryKey: commentKeys.commentsByCommentable(
            variables.commentable_id,
            variables.commentable_type,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: commentKeys.commentsWithReplies(
            variables.commentable_id,
            variables.commentable_type,
          ),
        });
      }

      // If it's a reply, invalidate parent comment replies
      if (variables.parent_comment_id) {
        queryClient.invalidateQueries({
          queryKey: commentKeys.repliesByParent(variables.parent_comment_id),
        });
      }

      // Invalidate audit logs for the entity/product that this comment is on
      if (variables.commentable_id) {
        if (variables.commentable_type === 'DatabaseEntity') {
          queryClient.invalidateQueries({
            queryKey: auditKeys.auditLogsByEntity(variables.commentable_id),
            exact: false,
          });
        } else if (variables.commentable_type === 'DatabaseProduct') {
          queryClient.invalidateQueries({
            queryKey: auditKeys.auditLogsByProduct(variables.commentable_id),
            exact: false,
          });
        }
      }

      // Invalidate database-level audit logs since comments create database-level audit logs
      // We don't have the database_id directly, so invalidate all database audit logs
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey;
          return (
            Array.isArray(queryKey) &&
            queryKey[0] === 'audit' &&
            queryKey[1] === 'auditLogsByDatabase'
          );
        },
      });

      // Also invalidate all audit logs to ensure they refresh
      queryClient.invalidateQueries({
        queryKey: auditKeys.auditLogs(),
        exact: false,
      });
    },
  });
};

// Mutation hook to update comment
export const useUpdateComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updateData }: { id: string; updateData: IUpdateComment }) =>
      updateComment(id, updateData),
    onSuccess: (data, variables) => {
      // Invalidate and refetch comment-related queries
      queryClient.invalidateQueries({ queryKey: commentKeys.all() });

      // Update the specific comment in cache if possible
      if (variables.id) {
        queryClient.invalidateQueries({
          queryKey: commentKeys.commentById(variables.id),
        });
      }

      // Invalidate database-level audit logs since comments create database-level audit logs
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey;
          return (
            Array.isArray(queryKey) &&
            queryKey[0] === 'audit' &&
            queryKey[1] === 'auditLogsByDatabase'
          );
        },
      });

      // Invalidate audit logs - we need to get the commentable info from the response or store it
      // For now, invalidate all audit logs since we don't have commentable info in update
      queryClient.invalidateQueries({
        queryKey: auditKeys.auditLogs(),
        exact: false,
      });
    },
  });
};

// Mutation hook to delete comment
export const useDeleteComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteComment(id),
    onSuccess: (data, variables) => {
      // Invalidate and refetch comment-related queries
      queryClient.invalidateQueries({ queryKey: commentKeys.all() });

      // Remove the specific comment from cache
      queryClient.removeQueries({
        queryKey: commentKeys.commentById(variables),
      });

      // Invalidate database-level audit logs since comments create database-level audit logs
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey;
          return (
            Array.isArray(queryKey) &&
            queryKey[0] === 'audit' &&
            queryKey[1] === 'auditLogsByDatabase'
          );
        },
      });

      // Invalidate audit logs - we need to get the commentable info from the response or store it
      // For now, invalidate all audit logs since we don't have commentable info in delete
      queryClient.invalidateQueries({
        queryKey: auditKeys.auditLogs(),
        exact: false,
      });
    },
  });
};
